import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function RMPercentageDetailReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = {};
    if (customerFilter !== "all") params.customer_name = customerFilter;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/reports/rm-percentage-detail", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.bpr_number || "").toLowerCase().includes(n) ||
        (r.customer_name || "").toLowerCase().includes(n) ||
        (r.product_name || "").toLowerCase().includes(n) ||
        (r.product_code || "").toLowerCase().includes(n) ||
        (r.rm_name || "").toLowerCase().includes(n) ||
        (r.rm_code || "").toLowerCase().includes(n) ||
        (r.supplier || "").toLowerCase().includes(n) ||
        (r.invoice_number || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [data, q]);

  const totals = data?.totals || {};

  const exportCSV = () => downloadCSV(
    "rm-percentage-detail",
    ["BPR Number", "Date", "Customer Name", "Product Name", "Product Code", "Order Qty", "Unit",
     "Raw Material Name", "Raw Material Code", "Supplier", "PO Number", "Width (mm)",
     "Issued Qty", "Issued Unit", "Rate", "Value of RM Used",
     "Invoice Date", "Invoice Number", "Taxable Value",
     "Wastage %", "RM %", "Difference", "Remarks", "Dispatched Qty", "Type"],
    rows.map((r) => [
      r.bpr_number, (r.date || "").slice(0, 10), r.customer_name, r.product_name, r.product_code,
      r.order_qty, r.unit, r.rm_name, r.rm_code, r.supplier, r.po_number, r.width_mm,
      r.issued_qty, r.issued_unit, r.rate, r.value_rm_used,
      (r.invoice_date || "").slice(0, 10), r.invoice_number, r.taxable_value,
      r.wastage_pct, r.rm_pct, r.difference, r.remarks, r.dispatched_qty, r.job_type,
    ])
  );

  const rmTone = totals.rm_pct >= 40 ? "danger" : totals.rm_pct >= 25 ? "warn" : "ok";

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="rmd-customer" className="rounded-sm w-64"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="rmd-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">From (dispatch)</label>
          <Input data-testid="rmd-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">To (dispatch)</label>
          <Input data-testid="rmd-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search MPWO / customer / RM / product / invoice</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="rmd-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. MPWO-25262 / ALV434 / Bimatoprost" />
          </div>
        </div>
        <Button data-testid="rmd-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="rmd-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi label="Jobs" value={inr(totals.jobs || 0)} />
        <Kpi label="Line items" value={inr(totals.line_items || 0)} />
        <Kpi label="Order qty" value={inr(totals.order_qty || 0)} />
        <Kpi label="Total RM value" value={`₹${inr(totals.value_rm_used || 0)}`} tone="warn" />
        <Kpi label="Taxable value" value={`₹${inr(totals.taxable_value || 0)}`} />
        <Kpi label="Weighted RM %" value={`${totals.rm_pct || 0}%`} tone={rmTone} />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-xs" data-testid="rmd-table">
          <thead className="bg-neutral-50 label-caps text-[9px] sticky top-0">
            <tr>
              <th className="text-left px-2 py-2">BPR</th>
              <th className="text-left px-2 py-2">Date</th>
              <th className="text-left px-2 py-2">Customer</th>
              <th className="text-left px-2 py-2">Product</th>
              <th className="text-left px-2 py-2">Product Code</th>
              <th className="text-right px-2 py-2">Order Qty</th>
              <th className="text-left px-2 py-2">Unit</th>
              <th className="text-left px-2 py-2">RM Name</th>
              <th className="text-left px-2 py-2">RM Code</th>
              <th className="text-left px-2 py-2">Supplier</th>
              <th className="text-left px-2 py-2">PO No.</th>
              <th className="text-right px-2 py-2">Width (mm)</th>
              <th className="text-right px-2 py-2">Issued Qty</th>
              <th className="text-right px-2 py-2">Rate</th>
              <th className="text-right px-2 py-2 bg-yellow-100">Value RM</th>
              <th className="text-left px-2 py-2">Inv Date</th>
              <th className="text-left px-2 py-2">Inv No.</th>
              <th className="text-right px-2 py-2 bg-yellow-100">Taxable Value</th>
              <th className="text-right px-2 py-2 bg-yellow-100">Wastage %</th>
              <th className="text-right px-2 py-2 bg-yellow-100">RM %</th>
              <th className="text-right px-2 py-2 bg-yellow-100">Difference</th>
              <th className="text-left px-2 py-2">Remarks</th>
              <th className="text-right px-2 py-2">Dispatched Qty</th>
              <th className="text-left px-2 py-2">Type</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={24} msg="No jobs with materials-used entries for this filter." />}
            {rows.slice(0, 600).map((r, i) => {
              const rmTone = r.rm_pct >= 40 ? "text-rose-700 font-bold"
                : r.rm_pct >= 25 ? "text-amber-700 font-semibold"
                : "text-emerald-700";
              return (
                <tr key={`${r.bpr_number}-${r.rm_code}-${i}`} className="border-t border-neutral-100 data-row" data-testid={`rmd-row-${r.bpr_number}-${r.rm_code}`}>
                  <td className="px-2 py-1.5 font-mono-num">{r.bpr_number}</td>
                  <td className="px-2 py-1.5 font-mono-num text-neutral-600">{(r.date || "").slice(0, 10)}</td>
                  <td className="px-2 py-1.5 font-medium">{r.customer_name}</td>
                  <td className="px-2 py-1.5">{r.product_name || "—"}</td>
                  <td className="px-2 py-1.5 font-mono-num text-neutral-600">{r.product_code || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num">{inr(r.order_qty)}</td>
                  <td className="px-2 py-1.5 text-neutral-600">{r.unit}</td>
                  <td className="px-2 py-1.5">{r.rm_name}</td>
                  <td className="px-2 py-1.5 font-mono-num text-neutral-600">{r.rm_code}</td>
                  <td className="px-2 py-1.5 text-neutral-600">{r.supplier}</td>
                  <td className="px-2 py-1.5 font-mono-num text-neutral-600">{r.po_number || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num text-neutral-600">{r.width_mm || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num">{inr(r.issued_qty)} {r.issued_unit && <span className="text-[9px] text-neutral-500">{r.issued_unit}</span>}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num">{inr(r.rate)}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num bg-yellow-50 font-semibold">₹{inr(r.value_rm_used)}</td>
                  <td className="px-2 py-1.5 font-mono-num text-neutral-600">{(r.invoice_date || "").slice(0, 10) || "—"}</td>
                  <td className="px-2 py-1.5 font-mono-num text-neutral-600">{r.invoice_number || "—"}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num bg-yellow-50 font-semibold">₹{inr(r.taxable_value)}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num bg-yellow-50">{r.wastage_pct}%</td>
                  <td className={`px-2 py-1.5 text-right font-mono-num bg-yellow-50 ${rmTone}`}>{r.rm_pct}%</td>
                  <td className={`px-2 py-1.5 text-right font-mono-num bg-yellow-50 ${r.difference < 0 ? "text-rose-700 font-semibold" : "text-neutral-800"}`}>₹{inr(r.difference)}</td>
                  <td className="px-2 py-1.5 text-neutral-600">
                    {r.remarks && (
                      <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-widest ${
                        r.remarks === "Invoice" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}>{r.remarks}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono-num">{inr(r.dispatched_qty || 0)}</td>
                  <td className="px-2 py-1.5 text-neutral-600">{r.job_type || "—"}</td>
                </tr>
              );
            })}
            {rows.length > 600 && (
              <tr><td colSpan={24} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 600 of {rows.length} rows. Export CSV for full list.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        Yellow columns match your Excel highlight. <b>Value RM</b> = issued qty × rate.
        <b> RM %</b> = job material cost / taxable value × 100.
        <b> Wastage %</b> = production waste / (good + waste) × 100.
        <b> Difference</b> = taxable value − material cost (gross material margin).
      </p>
    </div>
  );
}


// ============================================================================
// 12. Order Inflow vs Dispatch Trend
// ============================================================================
